import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { simplified } from '../utils/stringComparison';
import './PlayGame.css';

// TODO: consider getting the entire game state from the server on page load, so we still have it if the user refreshes
const PlayGame = () => {
  const [guess, setGuess] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [waitingForPartner, setWaitingForPartner] = useState(false);
  const [gameState, setGameState] = useState({
    yourGuesses: [],
    partnerGuesses: [],
  });

  const navigate = useNavigate();
  const typingTimeoutRef = useRef(null);

  const sendTypingStatus = async (isTyping) => {
    try {
      await fetch('/api/updateTypingStatus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isTyping }),
        credentials: 'include',
      });
    } catch (error) {
      // Silent fail - don't break typing experience
      console.error('Error sending typing status:', error);
    }
  };

  const sendGuessToServer = async () => {
    console.log('Sending guess to server:', guess);

    // Stop typing indicator when submitting
    // TODO: this should be done server side, otherwise it wil be racy and janky
    sendTypingStatus(false);

    new Audio('/confirm.mp3').play();

    try {
      const response = await fetch('/api/makeGuess', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guess }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData.message || 'Unknown error');
        setStatusMessage('Error: ' + (errorData.message || 'Failed to submit guess'));
        return;
      }

      const data = await response.json();

      if (data.otherPlayersGuess !== null) {
        setStatusMessage(data.otherPlayersGuess);

        // Update game state with the new guesses
        setGameState((prevState) => ({
          ...prevState,
          yourGuesses: [...prevState.yourGuesses, guess],
          partnerGuesses: [...prevState.partnerGuesses, data.otherPlayersGuess],
        }));

        if (simplified(data.otherPlayersGuess) === simplified(guess)) {
          navigate('/win');
        }

        // Reset for new round
        resetForNewRound();
      } else {
        setWaitingForPartner(true);
        setStatusMessage('Waiting for partner...');
        startCheckForPartnersGuess();
      }
    } catch (error) {
      console.error('Error:', error);
      setStatusMessage('Error: Could not connect to server');
    }
  };

  // TODO, restart the check at the begiining of every new round
  const startCheckForPartnersGuess = () => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch('/api/getPartnersGuess');

        // TODO: move to helper
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Server error:', errorData.message || 'Unknown error');
          setStatusMessage('Error: ' + (errorData.message || 'Failed to get partner\'s guess'));
          setWaitingForPartner(false);
          clearInterval(interval);
          return;
        }

        const data = await response.json();

        // Update status message based on partner's status
        // TODO: move to helper
        if (data.partnerStatus === 'typing') {
          setStatusMessage('Partner is typing...');
        } else if (data.partnerStatus === 'submitted') {
          setStatusMessage('Partner has submitted!');
        } else if (data.partnerStatus === 'idle') {
          setStatusMessage('Waiting for partner...');
        }

        if (data.otherPlayersGuess !== null) {
          setStatusMessage(data.otherPlayersGuess);
          clearInterval(interval);

          // Update game state with the partner's guess
          setGameState((prevState) => ({
            ...prevState,
            yourGuesses: [...prevState.yourGuesses, guess],
            partnerGuesses: [...prevState.partnerGuesses, data.otherPlayersGuess],
          }));

          if (simplified(data.otherPlayersGuess) === simplified(guess)) {
            navigate('/win');
          }

          resetForNewRound();
        }
      } catch (error) {
        console.error('Error:', error);
        setStatusMessage('Error: Could not connect to server');
        setWaitingForPartner(false);
        clearInterval(interval);
      }
    }, 1000);
  };

  const resetForNewRound = () => {
    // Reset for new round
    setGuess('');
    setStatusMessage('');
    setWaitingForPartner(false);

    new Audio('/next_round.mp3').play();
  }

  // Submitting guess to server should be diabled if still waiting for response from partner
  // for previous round, or if the player's current guess is an empty string.
  const isSubmitDisabled = waitingForPartner || !guess.trim();

  return (
    <div className="card">
      <div className="game-info">
        <div className="past-rounds">
          <h3><span className="subtitle">Past Rounds</span></h3>
          <div className="guess-container">
            <div className="guess-column">
              <p><span className="your-guess-subtitle">Your past guesses:</span></p>
              <div className="guess-cards">
                {gameState.yourGuesses.length > 0
                  ? gameState.yourGuesses.map((guess, index) => (
                      <div key={index} className="guess-card">{guess}</div>
                    ))
                  : <p>None yet</p>}
              </div>
            </div>
            <div className="guess-column">
              <p><span className="their-guess-subtitle">Partner's past guesses:</span></p>
              <div className="guess-cards">
                {gameState.partnerGuesses.length > 0
                  ? gameState.partnerGuesses.map((guess, index) => (
                      <div key={index} className="guess-card">{guess}</div>
                    ))
                  : <p>None yet</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="current-round">
          <h3><span className="subtitle">Round {gameState.yourGuesses.length + 1}</span></h3>
          <div className="guess-container no-border">
            <div className="guess-column">
              <div className="guess-card current">
                {guess || 'Your turn...'}
              </div>
            </div>
            <div className="guess-column">
              <div className="guess-card current">
                {statusMessage || 'Waiting...'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="input-container">
        <input
          type="text"
          id="textInput"
          value={guess}
          onChange={(e) => {
            // TODO: move everything to helper

            setGuess(e.target.value.toUpperCase());

            // Send typing status on every keystroke
            sendTypingStatus(true);

            // Clear existing timeout
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }

            // Set new timeout to mark as not typing after 2 seconds of inactivity
            typingTimeoutRef.current = setTimeout(() => {
              sendTypingStatus(false);
            }, 2000);
          }}
          onKeyUp={(e) => {
            if (e.key === 'Enter' && !isSubmitDisabled) {
              sendGuessToServer();
            }
          }}
          placeholder="Type your guess..."
          className="guess-textbox"
        />
        <button onClick={sendGuessToServer} className="button" disabled={isSubmitDisabled}>
          Confirm
        </button>
      </div>
    </div>
  );
};

export default PlayGame;
